import { type FormEvent, type JSX, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/icons';
import { ImportModal } from '@/components/admin/import-modal';
import {
  Badge,
  Button,
  Card,
  ConfirmModal,
  IconAction,
  Input,
  Skeleton,
  Spinner,
  Textarea,
} from '@/components/ui';
import {
  usePreviewImportPlans,
  useRunImportPlans,
} from '@/hooks/useAdminImports';
import {
  useCreatePlan,
  useDeletePlan,
  usePlanSubscribers,
  usePlans,
  useSetPlanVoiceTier,
  useUpdatePlan,
  type Plan,
  type PlanColor,
  type PlanInput,
  type PlanSubscriber,
  type PlanVoiceTier,
} from '@/hooks/useAdminPlans';
import { useRateCards } from '@/hooks/useAdminRateCards';
import { useAuth } from '@/hooks/useAuth';
import { ApiError } from '@/lib/api';
import { can } from '@/lib/permissions';
import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Reseller admin → Plans. Lives at /admin/plans, gated by RequireReseller
// like the rest of /v1/admin/*. Each reseller curates its own catalog;
// customers will (in a follow-up) get assigned a plan from the catalog
// via /admin/customers/:id. Today the page lets the reseller create,
// edit, soft-disable, and delete plans.
// ---------------------------------------------------------------------------
export default function AdminPlansPage(): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);
  const { user } = useAuth();
  const canEdit = can.useAdminModule(user?.role);
  const list = usePlans();
  const subscribers = usePlanSubscribers();
  const remove = useDeletePlan();

  const [editing, setEditing] = useState<Plan | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Plan | null>(null);

  const previewImport = usePreviewImportPlans();
  const runImport = useRunImportPlans();
  // Surfaced as a banner above the cards. The most useful failure here
  // is the 409 "still has subscribers" — silently closing the modal would
  // leave the operator wondering why the row didn't disappear.
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const items = list.data?.items ?? [];
  const subRows = subscribers.data?.items ?? [];

  const totalSubscribers = subRows.reduce((acc, s) => acc + s.subscribers, 0);

  const openNew = (): void => {
    setEditing(null);
    setShowForm(true);
  };
  const openEdit = (plan: Plan): void => {
    setEditing(plan);
    setShowForm(true);
  };
  const closeForm = (): void => {
    setShowForm(false);
    setEditing(null);
  };

  const handleDelete = async (): Promise<void> => {
    if (!pendingDelete) return;
    setDeleteError(null);
    try {
      await remove.mutateAsync(pendingDelete.id);
      setPendingDelete(null);
    } catch (err) {
      // Surface the message — most importantly the 409 "still has
      // subscribers" hint — as a banner above the cards. Closing the
      // confirm modal here so the banner is visible.
      setDeleteError(err instanceof ApiError ? err.message : t('apl_err_generic'));
      setPendingDelete(null);
    }
  };

  return (
    <div className="px-8 py-8 max-w-[1200px] mx-auto">
      <header className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="display text-[28px] font-semibold tracking-tight text-text leading-none m-0">
            {t('apl_title')}
          </h1>
          <p className="text-[13px] text-text-3 mt-1.5">
            {items.length === 0
              ? t('apl_sub_empty')
              : t('apl_sub', { total: totalSubscribers })}
          </p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setShowImport(true)}>
              {t('apl_import')}
            </Button>
            <Button onClick={openNew}>
              <Icon.plus width="14" height="14" />
              {t('apl_new')}
            </Button>
          </div>
        )}
      </header>

      {deleteError && (
        <div
          role="alert"
          className="mb-4 flex items-start gap-3 px-4 py-3 bg-danger-bg border border-danger-bd rounded-md"
        >
          <Icon.warn width="16" height="16" className="text-danger flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-[13px] text-danger">{deleteError}</div>
          <button
            type="button"
            onClick={() => setDeleteError(null)}
            className="text-[12px] text-danger/80 hover:text-danger"
            aria-label={t('apl_dismiss')}
          >
            ×
          </button>
        </div>
      )}

      {list.isLoading ? (
        // Flex + justify-center so 1-2 plans don't dangle on the left
        // when the grid would otherwise stretch to 3 columns. Each
        // card capped at ~380px so the visual rhythm matches the
        // pricing-page convention (Stripe / Linear).
        <div className="flex flex-wrap justify-center gap-4 mb-6">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-[360px] w-full max-w-[380px] md:basis-[calc(50%-0.5rem)] lg:basis-[calc(33.333%-0.667rem)]" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <PlansEmpty onCreate={openNew} canEdit={canEdit} />
      ) : (
        <>
          <div className="flex flex-wrap justify-center gap-4 mb-6">
            {items.map((p) => (
              <div
                key={p.id}
                className="w-full max-w-[380px] md:basis-[calc(50%-0.5rem)] lg:basis-[calc(33.333%-0.667rem)] flex"
              >
                <PlanCard
                  plan={p}
                  onEdit={() => openEdit(p)}
                  onDelete={() => setPendingDelete(p)}
                  canEdit={canEdit}
                />
              </div>
            ))}
          </div>

          <SubscribersByPlanCard
            subscribers={subRows}
            isLoading={subscribers.isLoading}
            lang={lang}
          />
        </>
      )}

      {showForm && (
        <PlanFormModal
          editing={editing}
          onClose={closeForm}
          onSaved={closeForm}
        />
      )}

      <ConfirmModal
        open={pendingDelete !== null}
        title={t('apl_delete_title')}
        description={
          pendingDelete
            ? t('apl_delete_desc', { name: pendingDelete.name })
            : ''
        }
        confirmLabel={t('apl_delete_action')}
        cancelLabel={t('apl_delete_cancel')}
        tone="danger"
        busy={remove.isPending}
        onConfirm={() => void handleDelete()}
        onClose={() => setPendingDelete(null)}
      />

      {showImport && (
        <ImportModal
          title={t('apl_import_title')}
          subtitle={t('apl_import_sub')}
          schemaHint={t('apl_import_schema_hint')}
          onClose={() => setShowImport(false)}
          onPreview={(file) => previewImport.mutateAsync(file)}
          onImport={(file) => runImport.mutateAsync(file)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Plan card
// ---------------------------------------------------------------------------
interface PlanCardProps {
  plan: Plan;
  onEdit: () => void;
  onDelete: () => void;
  canEdit: boolean;
}
function PlanCard({ plan, onEdit, onDelete, canEdit }: PlanCardProps): JSX.Element {
  const { t } = useTranslation('default');
  const isInk = plan.color === 'ink';
  const isVolt = plan.color === 'volt';

  return (
    <Card
      padding="none"
      className={cn(
        'relative overflow-hidden flex flex-col w-full',
        isVolt && 'border-volt-500 border-[1.5px]',
        isInk && 'border-ink border-[1.5px] bg-ink-2 text-white',
      )}
    >
      {plan.is_popular && (
        <div className="absolute top-3.5 right-3.5 px-2 py-0.5 bg-volt text-volt-ink text-[10px] font-bold uppercase tracking-[0.06em] rounded-sm">
          {t('apl_popular')}
        </div>
      )}

      {!plan.is_active && (
        <div className="absolute top-3.5 left-3.5">
          <Badge variant="neutral">{t('apl_inactive')}</Badge>
        </div>
      )}

      <div className="px-5.5 pt-5 pb-4 flex-1">
        {/* Pricing-tier convention (Stripe / Linear): center-align the
            identity block (name + tagline + price) so the card reads
            as a vertical poster. KV details + features below stay
            left-aligned for legibility (labels need a consistent
            left edge to scan). */}
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <div
            className={cn(
              'display text-[18px] font-semibold tracking-tight',
              isInk ? 'text-white' : 'text-text',
            )}
          >
            {plan.name}
          </div>
          <ChannelBadge plan={plan} isInk={isInk} />
        </div>
        {plan.tagline && (
          <div
            className={cn(
              'text-[12.5px] mt-1 leading-relaxed min-h-[36px] text-center',
              isInk ? 'text-white/60' : 'text-text-3',
            )}
          >
            {plan.tagline}
          </div>
        )}

        <div className="mt-4.5 flex items-baseline justify-center gap-1 num">
          {plan.included_sms === null || plan.included_sms === undefined ? (
            <span
              className={cn(
                'display text-[34px] font-semibold tracking-tight',
                isInk ? 'text-white' : 'text-text',
              )}
            >
              {t('apl_custom')}
            </span>
          ) : (
            <>
              <span
                className={cn(
                  'text-[18px] font-medium',
                  isInk ? 'text-white/60' : 'text-text-3',
                )}
              >
                $
              </span>
              <span
                className={cn(
                  'display text-[40px] font-semibold tracking-tight leading-none',
                  isInk ? 'text-white' : 'text-text',
                )}
              >
                {plan.monthly_fee}
              </span>
              <span
                className={cn(
                  'text-[13px] ml-1',
                  isInk ? 'text-white/50' : 'text-text-3',
                )}
              >
                {t('apl_per_month')}
              </span>
            </>
          )}
        </div>

        <div
          className={cn(
            'mt-4 px-3 py-2.5 rounded-md text-[12.5px] border',
            isInk
              ? 'bg-white/5 border-white/10'
              : 'bg-surface-2 border-line-soft',
          )}
        >
          <KV
            label={t('apl_included')}
            value={
              plan.included_sms === null || plan.included_sms === undefined
                ? t('apl_negotiated')
                : t('apl_included_value', {
                    count: Math.round(plan.included_sms / 1000),
                  })
            }
            isInk={isInk}
          />
          <KV
            label={t('apl_overage')}
            value={t('apl_overage_value', {
              price: plan.overage_per_sms.toFixed(4),
            })}
            isInk={isInk}
            last={
              plan.included_voice_minutes === null ||
              plan.included_voice_minutes === undefined
            }
          />
          {plan.included_voice_minutes !== null &&
            plan.included_voice_minutes !== undefined && (
              <KV
                label={t('apl_voice_included')}
                value={t('apl_voice_included_value', {
                  count: plan.included_voice_minutes,
                })}
                isInk={isInk}
                last
              />
            )}
        </div>

        <div
          className={cn(
            'mt-4 pt-4 border-t pl-4',
            isInk ? 'border-white/10' : 'border-line-soft',
          )}
        >
          {plan.features.length === 0 ? (
            <p
              className={cn(
                'text-[12px] italic',
                isInk ? 'text-white/40' : 'text-text-4',
              )}
            >
              {t('apl_no_features')}
            </p>
          ) : (
            plan.features.map((f, i) => (
              <div
                key={i}
                className={cn(
                  'flex items-start gap-2 py-1 text-[13px]',
                  isInk ? 'text-white/85' : 'text-text-2',
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full',
                    isVolt
                      ? 'bg-volt-500'
                      : isInk
                        ? 'bg-volt-400'
                        : 'bg-success',
                  )}
                />
                <span>{f}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Footer = actions only. The price was duplicating the big
          number in the body and disagreed with "Custom" pricing (it
          showed $0.00 / mo for negotiated plans because monthly_fee
          defaults to 0). Removed to eliminate both issues. */}
      <div
        className={cn(
          'px-3 py-2 border-t flex justify-end items-center gap-1',
          isInk
            ? 'border-white/10 bg-white/5'
            : 'border-line-soft bg-surface-2',
        )}
      >
        <Link
          to={`/admin/plans/${plan.id}/overrides`}
          title={t('apl_overrides')}
          aria-label={t('apl_overrides')}
          className={cn(
            'w-7 h-7 grid place-items-center rounded-md transition-colors',
            isInk
              ? 'text-white/80 hover:text-white hover:bg-white/15'
              : 'text-text-3 hover:text-text hover:bg-surface-3',
          )}
        >
          <Icon.cog width={16} height={16} />
        </Link>
        {canEdit && (
          <>
            <IconAction icon={Icon.pencil} label={t('apl_edit')} onClick={onEdit} />
            <IconAction icon={Icon.trash} label={t('apl_delete')} onClick={onDelete} tone="danger" />
          </>
        )}
      </div>
    </Card>
  );
}

interface KVProps {
  label: string;
  value: React.ReactNode;
  isInk: boolean;
  last?: boolean;
}
// ChannelBadge — small pill next to the plan name announcing which
// channels the plan bundles. Lets a reseller scanning their catalog
// distinguish SMS-only / voice-only / bundle at a glance.
function ChannelBadge({ plan, isInk }: { plan: Plan; isInk: boolean }): JSX.Element | null {
  const { t } = useTranslation('default');
  const hasSms = plan.included_sms !== null && plan.included_sms !== undefined;
  const hasVoice =
    plan.included_voice_minutes !== null && plan.included_voice_minutes !== undefined;
  if (!hasSms && !hasVoice) return null;
  let label: string;
  if (hasSms && hasVoice) label = t('apl_channel_bundle');
  else if (hasSms) label = t('apl_channel_sms_only');
  else label = t('apl_channel_voice_only');
  return (
    <span
      className={cn(
        'text-[10.5px] font-medium px-1.5 py-[1px] rounded uppercase tracking-[0.04em]',
        isInk ? 'bg-white/10 text-white/80' : 'bg-volt-glow text-volt-700',
      )}
    >
      {label}
    </span>
  );
}

function KV({ label, value, isInk, last = false }: KVProps): JSX.Element {
  return (
    <div className={cn('flex justify-between', !last && 'mb-1')}>
      <span className={cn(isInk ? 'text-white/60' : 'text-text-3')}>{label}</span>
      <span className={cn('num font-semibold', isInk ? 'text-white' : 'text-text')}>
        {value}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Subscribers by plan table
// ---------------------------------------------------------------------------
interface SubscribersByPlanCardProps {
  subscribers: PlanSubscriber[];
  isLoading: boolean;
  lang: string;
}
function SubscribersByPlanCard({
  subscribers,
  isLoading,
  lang,
}: SubscribersByPlanCardProps): JSX.Element {
  const { t } = useTranslation('default');
  // Match the implied width of a full row of 3 plan cards
  // (3 × 380 + 2 × 16 = 1172) so the subscribers table doesn't
  // stretch edge-to-edge while the cards above sit in a narrow
  // centered band.
  // Narrower than the page max-width so the table doesn't dangle
  // when the cards row above is only 1-2 plans. 800px matches a
  // typical reporting widget — wide enough to fit the numbers
  // without making the empty horizontal gap distracting.
  return (
    <div className="max-w-[800px] mx-auto">
      {/* Header: stacked (title + subtitle) instead of left/right split.
          With the narrower card, the right-floated subtitle dangled
          oddly. Stacking centers the visual gravity on the title. */}
      <div className="px-5 pt-4 pb-2">
        <h2 className="display text-[13px] uppercase tracking-[0.06em] text-text-3 font-semibold m-0">
          {t('apl_subscribers_title')}
        </h2>
        <p className="text-[11.5px] text-text-4 mt-0.5">{t('apl_subscribers_sub')}</p>
      </div>
      <Card padding="none">
        {isLoading ? (
          <div className="p-4 space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        ) : subscribers.length === 0 ? (
          <p className="p-8 text-center text-[13px] text-text-3">
            {t('apl_subscribers_empty')}
          </p>
        ) : (
          // Compact rows matching the pattern used in campaigns / flows
          // / IVR pages. Grid gives us crisp column alignment without
          // the visual overhead of a thead.
          <div>
            <div className="grid grid-cols-[minmax(0,1fr)_120px_140px] gap-3 px-4 py-2 border-b border-line text-[10px] uppercase tracking-[0.06em] text-text-4 font-medium bg-surface-2">
              <span>{t('apl_sub_col_plan')}</span>
              <span className="text-right">{t('apl_sub_col_customers')}</span>
              <span className="text-right">{t('apl_sub_col_mrr')}</span>
            </div>
            {subscribers.map((s, idx) => (
              <div
                key={s.plan_id}
                className={cn(
                  'grid grid-cols-[minmax(0,1fr)_120px_140px] gap-3 items-center px-4 py-2.5',
                  'hover:bg-surface-2 transition-colors',
                  idx !== subscribers.length - 1 && 'border-b border-line',
                )}
              >
                <div className="min-w-0 flex items-center gap-2.5">
                  <span
                    aria-hidden="true"
                    className={cn(
                      'w-2 h-2 rounded-sm shrink-0',
                      s.color === 'volt'
                        ? 'bg-volt-500'
                        : s.color === 'ink'
                          ? 'bg-ink'
                          : 'bg-text-3',
                    )}
                  />
                  <span className="text-[13px] text-text font-medium truncate">
                    {s.name}
                  </span>
                </div>
                <span className="text-[13px] tabular-nums text-right text-text-2">
                  {s.subscribers.toLocaleString(lang)}
                </span>
                <span className="text-[13px] tabular-nums text-right text-text font-semibold">
                  {formatCurrency(s.mrr, lang)}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------
function PlansEmpty({ onCreate, canEdit }: { onCreate: () => void; canEdit: boolean }): JSX.Element {
  const { t } = useTranslation('default');
  return (
    <Card padding="lg">
      <div className="py-12 px-8 flex flex-col items-center text-center">
        <h3 className="display text-[22px] font-semibold text-text m-0 tracking-tight">
          {t('apl_empty_title')}
        </h3>
        <p className="text-[14px] text-text-3 mt-2.5 mb-5 max-w-[420px] leading-relaxed">
          {t('apl_empty_sub')}
        </p>
        {canEdit && (
          <Button onClick={onCreate}>
            <Icon.plus width="14" height="14" />
            {t('apl_empty_cta')}
          </Button>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Form modal — used for both create and edit. Edit pre-fills from
// `editing`; create starts blank with sensible defaults.
// ---------------------------------------------------------------------------
interface PlanFormModalProps {
  editing: Plan | null;
  onClose: () => void;
  onSaved: () => void;
}
function PlanFormModal({ editing, onClose, onSaved }: PlanFormModalProps): JSX.Element {
  const { t } = useTranslation('default');
  const { user } = useAuth();
  const isPlatformAdmin = user?.tenantType === 'platform';
  const create = useCreatePlan();
  const update = useUpdatePlan();
  const setVoiceTier = useSetPlanVoiceTier();
  const rateCards = useRateCards();
  const isEdit = editing !== null;
  const submitting = create.isPending || update.isPending || setVoiceTier.isPending;

  const [form, setForm] = useState<{
    name: string;
    tagline: string;
    monthlyFee: string;
    includedSms: string; // empty string = "negotiated"
    overagePerSms: string;
    featuresText: string; // newline-separated in the textarea
    isPopular: boolean;
    color: PlanColor;
    isActive: boolean;
    // Empty string = "use the reseller's default rate card" (null on the wire).
    rateCardId: string;
    includeSms: boolean;
    includeVoice: boolean;
    includedVoiceMinutes: string;
    voiceTierMax: PlanVoiceTier;
  }>(() => ({
    name: editing?.name ?? '',
    tagline: editing?.tagline ?? '',
    monthlyFee: editing ? String(editing.monthly_fee) : '',
    includedSms:
      editing?.included_sms === null || editing?.included_sms === undefined
        ? ''
        : String(editing.included_sms),
    overagePerSms: editing ? String(editing.overage_per_sms) : '',
    featuresText: editing?.features.join('\n') ?? '',
    isPopular: editing?.is_popular ?? false,
    color: editing?.color ?? 'neutral',
    isActive: editing?.is_active ?? true,
    rateCardId:
      editing?.rate_card_id === null || editing?.rate_card_id === undefined
        ? ''
        : String(editing.rate_card_id),
    // Channel toggles + voice allotment. SMS defaults ON for backwards-
    // compat with existing plans (they all had an included_sms before
    // PR-1 of this stream). Voice defaults to whatever the edit row
    // has; new plans start with voice OFF so the operator has to
    // explicitly opt in.
    includeSms: editing
      ? editing.included_sms !== null && editing.included_sms !== undefined
      : true,
    includeVoice: editing
      ? editing.included_voice_minutes !== null &&
        editing.included_voice_minutes !== undefined
      : false,
    includedVoiceMinutes:
      editing?.included_voice_minutes === null ||
      editing?.included_voice_minutes === undefined
        ? ''
        : String(editing.included_voice_minutes),
    // voice_tier_max gates which voice_catalog.tier rows the plan's
    // customers can see in the AI Agent profile editor. Defaults to
    // 'standard' (matches the DB default in migration 69) so new plans
    // start with the OpenAI-tts-1 catalog only — admin opts in to
    // premium (Deepgram Aura-2) explicitly.
    voiceTierMax: editing?.voice_tier_max ?? 'standard',
  }));
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);

    // Empty fee / overage = "this plan doesn't charge that" → treat
    // as 0 (matches DB default). Only reject negative / NaN.
    const monthlyFeeRaw = form.monthlyFee.trim();
    const fee = monthlyFeeRaw === '' ? 0 : parseFloat(monthlyFeeRaw);
    const overagePerSmsRaw = form.overagePerSms.trim();
    const overage = overagePerSmsRaw === '' ? 0 : parseFloat(overagePerSmsRaw);
    // SMS allotment: only consumed when includeSms is on. Empty input
    // = "negotiated" (NULL) → unlimited / Enterprise-style. The
    // channel toggle is the gate; the number is the allotment.
    const includedSmsRaw = form.includedSms.trim();
    const includedSms = !form.includeSms
      ? null
      : includedSmsRaw === ''
        ? null
        : Number(includedSmsRaw);
    const includedVoiceRaw = form.includedVoiceMinutes.trim();
    const includedVoice = !form.includeVoice
      ? null
      : includedVoiceRaw === ''
        ? null
        : Number(includedVoiceRaw);
    const features = form.featuresText
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l !== '');

    if (form.name.trim() === '') {
      setError(t('apl_err_name'));
      return;
    }
    if (!Number.isFinite(fee) || fee < 0) {
      setError(t('apl_err_fee'));
      return;
    }
    if (!Number.isFinite(overage) || overage < 0) {
      setError(t('apl_err_overage'));
      return;
    }
    if (includedSms !== null && (!Number.isInteger(includedSms) || includedSms < 0)) {
      setError(t('apl_err_included'));
      return;
    }
    if (includedVoice !== null && (!Number.isInteger(includedVoice) || includedVoice < 0)) {
      setError(t('apl_err_included_voice'));
      return;
    }
    if (!form.includeSms && !form.includeVoice) {
      setError(t('apl_err_no_channel'));
      return;
    }

    const input: PlanInput = {
      name: form.name.trim(),
      tagline: form.tagline.trim() || undefined,
      monthly_fee: fee,
      included_sms: includedSms,
      overage_per_sms: overage,
      included_voice_minutes: includedVoice,
      features,
      is_popular: form.isPopular,
      color: form.color,
      is_active: form.isActive,
      rate_card_id: form.rateCardId === '' ? null : Number(form.rateCardId),
    };

    try {
      let savedID: number;
      if (isEdit && editing) {
        const updated = await update.mutateAsync({ id: editing.id, input });
        savedID = updated.id;
      } else {
        const created = await create.mutateAsync(input);
        savedID = created.id;
      }
      // Voice tier lives on a separate endpoint (raw-pgx, not sqlc —
      // see api/internal/services/admin_plans_voice_tier.go). Only
      // platform admins see the dropdown; gating the side call on
      // isPlatformAdmin keeps a reseller-admin save from 403-ing on a
      // hidden field. We only call the endpoint when the tier actually
      // changes from whatever the row already has (DB default for new
      // plans is 'standard') so a no-op save doesn't burn a round trip.
      if (isPlatformAdmin) {
        const currentTier: PlanVoiceTier =
          editing?.voice_tier_max ?? 'standard';
        if (form.voiceTierMax !== currentTier) {
          try {
            await setVoiceTier.mutateAsync({
              id: savedID,
              tier: form.voiceTierMax,
            });
          } catch (err) {
            // Surface the failure but don't roll back the plan save —
            // the plan itself persisted, only the tier flip failed.
            setError(
              err instanceof ApiError
                ? err.message
                : t('apl_err_voice_tier_generic'),
            );
            return;
          }
        }
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('apl_err_generic'));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-ink/50 px-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-line rounded-lg shadow-lg w-full max-w-[560px] max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={(e) => void submit(e)}>
          <div className="px-5 py-4 border-b border-line">
            <h2 className="display text-[16px] font-semibold text-text">
              {isEdit ? t('apl_form_edit_title') : t('apl_form_new_title')}
            </h2>
            <p className="text-[12px] text-text-3 mt-0.5">{t('apl_form_sub')}</p>
          </div>

          <div className="px-5 py-4 space-y-4">
            {error && (
              <div className="px-3 py-2 bg-danger-bg border border-danger-bd rounded-md text-[13px] text-danger">
                {error}
              </div>
            )}

            <Field label={t('apl_field_name')}>
              <Input
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="Growth"
                maxLength={80}
                required
              />
            </Field>

            <Field label={t('apl_field_tagline')} hint={t('apl_field_tagline_hint')}>
              <Input
                value={form.tagline}
                onChange={(e) => setForm((p) => ({ ...p, tagline: e.target.value }))}
                placeholder={t('apl_field_tagline_placeholder')}
                maxLength={200}
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label={t('apl_field_fee')} hint={t('apl_field_fee_hint', 'Leave empty for $0 / pay-as-you-go plans')}>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.monthlyFee}
                  onChange={(e) => setForm((p) => ({ ...p, monthlyFee: e.target.value }))}
                  placeholder="0"
                />
              </Field>
              <Field label={t('apl_field_overage')} hint={t('apl_field_overage_hint', 'Per-SMS price after included allotment; leave empty for $0')}>
                <Input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={form.overagePerSms}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, overagePerSms: e.target.value }))
                  }
                  placeholder="0.0000"
                  disabled={!form.includeSms}
                />
              </Field>
            </div>

            {/* Channel toggles — SMS / Voice. At least one must be on
                (validated on submit). When OFF the related allotment +
                overage inputs go disabled but keep their value so the
                operator can toggle without losing what they typed. */}
            <div className="border border-line rounded-md divide-y divide-line">
              <label className="flex items-start gap-3 p-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.includeSms}
                  onChange={(e) => setForm((p) => ({ ...p, includeSms: e.target.checked }))}
                  className="mt-1"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-text">
                    {t('apl_channel_sms')}
                  </div>
                  <div className="text-[11.5px] text-text-3 mt-0.5">
                    {t('apl_channel_sms_hint')}
                  </div>
                  {form.includeSms && (
                    <Field
                      label={t('apl_field_included')}
                      hint={t('apl_field_included_hint')}
                    >
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        value={form.includedSms}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, includedSms: e.target.value }))
                        }
                        placeholder="25000"
                      />
                    </Field>
                  )}
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.includeVoice}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, includeVoice: e.target.checked }))
                  }
                  className="mt-1"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-text">
                    {t('apl_channel_voice')}
                  </div>
                  <div className="text-[11.5px] text-text-3 mt-0.5">
                    {t('apl_channel_voice_hint')}
                  </div>
                  {form.includeVoice && (
                    <Field
                      label={t('apl_field_included_voice')}
                      hint={t('apl_field_included_voice_hint')}
                    >
                      <Input
                        type="number"
                        step="1"
                        min="0"
                        value={form.includedVoiceMinutes}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            includedVoiceMinutes: e.target.value,
                          }))
                        }
                        placeholder="500"
                      />
                    </Field>
                  )}
                </div>
              </label>
            </div>

            <Field label={t('apl_field_rate_card')} hint={t('apl_field_rate_card_hint')}>
              <select
                value={form.rateCardId}
                onChange={(e) => setForm((p) => ({ ...p, rateCardId: e.target.value }))}
                disabled={rateCards.isLoading}
                className="w-full h-9 px-3 text-[13px] rounded-md bg-surface border border-line text-text outline-none focus:border-volt-500 focus:ring-[3px] focus:ring-volt-glow"
              >
                <option value="">{t('apl_field_rate_card_default')}</option>
                {(rateCards.data?.items ?? []).map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {c.name}
                    {c.is_default ? ` (${t('apl_field_rate_card_default_pill')})` : ''}
                  </option>
                ))}
              </select>
            </Field>

            <Field label={t('apl_field_features')} hint={t('apl_field_features_hint')}>
              <Textarea
                value={form.featuresText}
                onChange={(e) =>
                  setForm((p) => ({ ...p, featuresText: e.target.value }))
                }
                placeholder={t('apl_field_features_placeholder')}
                rows={5}
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label={t('apl_field_color')}>
                <select
                  value={form.color}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, color: e.target.value as PlanColor }))
                  }
                  className="w-full h-9 px-3 text-[13px] rounded-md bg-surface border border-line text-text outline-none focus:border-volt-500 focus:ring-[3px] focus:ring-volt-glow"
                >
                  <option value="neutral">{t('apl_color_neutral')}</option>
                  <option value="volt">{t('apl_color_volt')}</option>
                  <option value="ink">{t('apl_color_ink')}</option>
                </select>
              </Field>
              {isPlatformAdmin ? (
                <Field
                  label={t('apl_field_voice_tier')}
                  hint={t('apl_field_voice_tier_hint')}
                >
                  <select
                    value={form.voiceTierMax}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        voiceTierMax: e.target.value as PlanVoiceTier,
                      }))
                    }
                    className="w-full h-9 px-3 text-[13px] rounded-md bg-surface border border-line text-text outline-none focus:border-volt-500 focus:ring-[3px] focus:ring-volt-glow"
                  >
                    <option value="standard">{t('apl_voice_tier_standard')}</option>
                    <option value="premium">{t('apl_voice_tier_premium')}</option>
                  </select>
                </Field>
              ) : (
                <div />
              )}
            </div>

            <div className="flex items-center gap-4">
              <label className="inline-flex items-center gap-2 text-[13px] text-text">
                <input
                  type="checkbox"
                  checked={form.isPopular}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, isPopular: e.target.checked }))
                  }
                />
                {t('apl_field_popular')}
              </label>
              <label className="inline-flex items-center gap-2 text-[13px] text-text">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, isActive: e.target.checked }))
                  }
                />
                {t('apl_field_active')}
              </label>
            </div>
          </div>

          <div className="px-5 py-4 border-t border-line flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={submitting}
            >
              {t('apl_form_cancel')}
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner />
                  <span>{t('apl_form_saving')}</span>
                </>
              ) : isEdit ? (
                t('apl_form_save')
              ) : (
                t('apl_form_create')
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface FieldProps {
  label: string;
  hint?: string;
  children: JSX.Element;
}
function Field({ label, hint, children }: FieldProps): JSX.Element {
  return (
    <div>
      <label className="block text-[12px] font-medium text-text-2 mb-1.5">
        {label}
      </label>
      {children}
      {hint && <p className="text-[12px] text-text-4 mt-1">{hint}</p>}
    </div>
  );
}
